import { useEffect } from 'react';
import { Dispatch } from 'redux';
import { MerchantBookingPageApiResponse } from '../../../../../interfaces/merchants/bookings';
import { SchemeType } from '../../../../../interfaces/merchants/products/purchases';
import * as Purchases from '../../../../reducers/merchants/products/purchases';
import * as Inputs from '../../../../reducers/merchants/products/purchases/inputs';
import * as Users from '../../../../reducers/users';

import * as aUser from '../../../users/api';
import * as a from './api';
import * as workflow from './workflow';
import { fetchMerchantDetail } from '../../../../../infrastructure/api';
import { fetchMerchantProductFirstCharge } from './api';
import { RootState } from '../../../..';
import { ActionCreator, ThunkAction } from '@reduxjs/toolkit';

export interface InitQuery {
  publicId: string;
  productId: string;
  isEmailVerified: boolean;
}

type AllActions = Purchases.Actions | Inputs.Actions | Users.Actions;

const toDataSet = async ({
  schemeType,
  productId,
  bookingPages,
}: {
  schemeType: string;
  productId: string;
  bookingPages: MerchantBookingPageApiResponse[];
}): Promise<Purchases.DataSet> => {
  switch (schemeType) {
    case SchemeType.Subscription:
      return {
        schemeType,
        customFieldsData: await a.fetchCustomFields(productId),
        confirmData: await a.fetchConfirmation(productId),
        useCaseData: {
          booking_pages: bookingPages,
        },
      };
    case SchemeType.TicketBook:
      return {
        schemeType,
        customFieldsData: await a.fetchCustomFields(productId),
        confirmData: await a.fetchConfirmation(productId),
        useCaseData: {
          booking_pages: bookingPages,
        },
      };
    default:
      throw new Error(`require new implementation of "${schemeType}"`);
  }
};

const toSubscribed = async ({
  schemeType,
  productId,
}: {
  schemeType: string;
  productId: string;
}): Promise<boolean> => {
  if (schemeType === SchemeType.Subscription) {
    const usersSubscriptionData = await aUser.fetchUsersSubscription(productId);
    return !!usersSubscriptionData;
  }
  return false;
};

export const init: ActionCreator<
  ThunkAction<void, RootState, undefined, AllActions>
> = (q: InitQuery) => async (dispatch: Dispatch<AllActions>) => {
  const { publicId, productId, isEmailVerified } = q;
  const usersMeData = await aUser.fetchUsersMe();
  const { merchant } = await fetchMerchantDetail({
    session: '',
    token: '',
    requestId: '',
    merchantPublicId: q.publicId,
  });
  const productData = await a.fetchProduct(
    merchant?.public_id ?? '',
    productId,
  );
  const firstCharge =
    productData.scheme === 0 // subscription schemeの場合
      ? await fetchMerchantProductFirstCharge(publicId, productId)
      : null;

  const dataSetAttr = {
    schemeType:
      productData.scheme === 0
        ? SchemeType.Subscription
        : SchemeType.TicketBook,
    productId,
    bookingPages: productData.booking_pages,
  };
  const dataSet = await toDataSet(dataSetAttr);
  const subscribed = await toSubscribed(dataSetAttr);
  if (usersMeData) {
    dispatch({
      payload: {
        creditCard: usersMeData.credit_card
          ? {
              canonicalId: usersMeData.credit_card.canonical_id,
              expireMonth: usersMeData.credit_card.expire_month,
              expireYear: usersMeData.credit_card.expire_year,
              tailDigits: usersMeData.credit_card.tail_digits,
            }
          : undefined,
        displayName: usersMeData.display_name,
        email: usersMeData.email,
        firstname: usersMeData.firstname,
        id: usersMeData.id,
        isEmailVerified: usersMeData.is_email_verified,
        lastname: usersMeData.lastname,
        phoneNumber: usersMeData.phone_number,
        publicId: usersMeData.public_id,
      },
      type: Users.Types.Me,
    });
  }
  dispatch({
    payload: { productId },
    type: Inputs.Types.Init,
  });

  if (merchant) {
    dispatch({
      payload: {
        ...q,
        dataSet,
        productData,
        loggedIn: !!usersMeData,
        subscribed,
        jcbEnabled: merchant.jcb_enabled,
        isEmailVerified,
        firstCharge,
      },
      type: Purchases.Types.Init,
    });
  }

  workflow.init();
};

export const nextStep =
  (step: number) => (dispatch: Dispatch<Purchases.Actions>) =>
    dispatch({
      type: Purchases.Types.SetStep,
      payload: {
        step: workflow.nextStep(step, false),
      },
    });

export const finalStep =
  (step: number) => (dispatch: Dispatch<Purchases.Actions>) =>
    dispatch({
      type: Purchases.Types.SetStep,
      payload: {
        step: workflow.nextStep(step, true),
      },
    });

export const goTo =
  (step: number) => (dispatch: Dispatch<Purchases.Actions>) => {
    dispatch({ type: Purchases.Types.SetStep, payload: { step } });
  };

export const setPostable =
  (postable: boolean) => (dispatch: Dispatch<Purchases.SetPostableAction>) => {
    dispatch({
      payload: { postable },
      type: Purchases.Types.SetPostable,
    });
  };

export const setMeToPurchaseInfo =
  (productId: string) => async (dispatch: Dispatch<AllActions>) => {
    dispatch({
      payload: { customFields: await a.fetchCustomFields(productId) },
      type: Purchases.Types.SetCustomFields,
    });
    const usersMeData = await aUser.fetchUsersMe();
    if (usersMeData) {
      dispatch({
        payload: {
          creditCard: usersMeData.credit_card
            ? {
                canonicalId: usersMeData.credit_card.canonical_id,
                expireMonth: usersMeData.credit_card.expire_month,
                expireYear: usersMeData.credit_card.expire_year,
                tailDigits: usersMeData.credit_card.tail_digits,
              }
            : undefined,
          displayName: usersMeData.display_name,
          email: usersMeData.email,
          firstname: usersMeData.firstname,
          id: usersMeData.id,
          isEmailVerified: usersMeData.is_email_verified,
          lastname: usersMeData.lastname,
          phoneNumber: usersMeData.phone_number,
          publicId: usersMeData.public_id,
        },
        type: Users.Types.Me,
      });
    }
  };

// browserの戻るボタンなどでpopstateイベントが発生した時に、storeの状態を更新するフック
// next/routerのbeforePopStateはpathが同じ場合には実行されないため、windowに直接イベントリスナを設定
// https://github.com/vercel/next.js/blob/v12.0.1/packages/next/shared/lib/router/router.ts#L825-L833
export const useWindowPopstate: (params: { dispatch: Dispatch }) => void = ({
  dispatch,
}) => {
  useEffect(() => {
    const onWindowPopstateHandler = () => {
      const payload = workflow.setup(history.state && history.state.options);
      if (payload) {
        dispatch({ payload, type: Purchases.Types.SetStep });
      }
    };

    window.addEventListener('popstate', onWindowPopstateHandler);

    return () => {
      window.removeEventListener('popstate', onWindowPopstateHandler);
    };
  }, [dispatch]);
};
